import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileText, Clock, CheckCircle, PenTool, Download,
  ArrowRight, Bell, User, HelpCircle, ChevronRight
} from 'lucide-react';

interface ClientDocument {
  id: string;
  name: string;
  templateName: string;
  status: 'draft' | 'ready' | 'signature_required' | 'signed' | 'complete';
  createdAt: Date;
  downloadUrl?: string;
  signatureUrl?: string;
}

interface ClientMatter {
  id: string;
  name: string;
  number: string;
  status: string;
  documentCount: number;
}

export function ClientHome() {
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [matters, setMatters] = useState<ClientMatter[]>([]);
  const [_loading, setLoading] = useState(true);
  const clientName = 'John'; // Would come from auth context

  useEffect(() => {
    loadClientData();
  }, []);

  const loadClientData = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));

    setDocuments([
      {
        id: '1',
        name: 'Warranty Deed - 123 Main St',
        templateName: 'Wisconsin Warranty Deed',
        status: 'signature_required',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2),
        signatureUrl: '/sign/1',
      },
      {
        id: '2',
        name: 'Operating Agreement',
        templateName: 'LLC Operating Agreement',
        status: 'ready',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24),
        downloadUrl: '#',
      },
      {
        id: '3',
        name: 'Property Deed - Previous',
        templateName: 'Wisconsin Warranty Deed',
        status: 'complete',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30),
        downloadUrl: '#',
      },
    ]);

    setMatters([
      {
        id: '1',
        name: 'Property Purchase - 123 Main St',
        number: '2024-0089-REP',
        status: 'Active',
        documentCount: 4,
      },
      {
        id: '2',
        name: 'LLC Formation',
        number: '2024-0092-BUF',
        status: 'Closing',
        documentCount: 3,
      },
    ]);

    setLoading(false);
  };

  const getStatusConfig = (status: ClientDocument['status']) => {
    const configs = {
      draft: { label: 'In Progress', color: 'text-yellow-400', bg: 'bg-yellow-500/20', icon: Clock },
      ready: { label: 'Ready for Review', color: 'text-blue-400', bg: 'bg-blue-500/20', icon: FileText },
      signature_required: { label: 'Signature Required', color: 'text-orange-400', bg: 'bg-orange-500/20', icon: PenTool },
      signed: { label: 'Signed', color: 'text-green-400', bg: 'bg-green-500/20', icon: CheckCircle },
      complete: { label: 'Complete', color: 'text-green-400', bg: 'bg-green-500/20', icon: CheckCircle },
    };
    return configs[status];
  };

  const pendingActions = documents.filter(d => 
    d.status === 'signature_required' || d.status === 'ready'
  ).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-900 flex items-center justify-center">
              <span className="text-accent-400 font-bold text-lg">L</span>
            </div>
            <div>
              <h1 className="font-semibold text-slate-900">Lawity</h1>
              <p className="text-xs text-slate-500">Client Portal</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <button className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg">
              <Bell className="w-5 h-5" />
              {pendingActions > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingActions}
                </span>
              )}
            </button>
            <button className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg">
              <HelpCircle className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 pl-4 border-l border-slate-200">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <User className="w-4 h-4 text-primary-600" />
              </div>
              <span className="text-sm text-slate-700">{clientName}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900">Welcome back, {clientName}</h2>
          <p className="text-slate-600 mt-1">
            {pendingActions > 0 
              ? `You have ${pendingActions} item${pendingActions > 1 ? 's' : ''} requiring your attention.`
              : 'All caught up! No pending actions.'}
          </p>
        </div>

        {/* Action Required Banner */}
        {documents.filter(d => d.status === 'signature_required').length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <PenTool className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="font-medium text-orange-900">Signature Required</p>
                <p className="text-sm text-orange-700">
                  {documents.filter(d => d.status === 'signature_required').length} document(s) awaiting your signature
                </p>
              </div>
            </div>
            <Link
              to="/documents?filter=signature_required"
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Sign Now
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Documents */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Recent Documents</h3>
                <Link to="/documents" className="text-sm text-primary-600 hover:text-primary-700">
                  View All
                </Link>
              </div>
              
              <div className="divide-y divide-slate-100">
                {documents.map((doc, idx) => {
                  const config = getStatusConfig(doc.status);
                  const Icon = config.icon;
                  
                  return (
                    <motion.div
                      key={doc.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="px-6 py-4 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg ${config.bg} flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${config.color}`} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{doc.name}</p>
                          <p className="text-sm text-slate-500">{doc.templateName}</p>
                        </div>
                        
                        <div className="text-right">
                          <span className={`text-sm font-medium ${config.color}`}>
                            {config.label}
                          </span>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {formatTimeAgo(doc.createdAt)}
                          </p>
                        </div>
                        
                        <div className="flex gap-2">
                          {doc.status === 'signature_required' && (
                            <Link
                              to={doc.signatureUrl!}
                              className="px-3 py-1.5 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700"
                            >
                              Sign
                            </Link>
                          )}
                          {doc.downloadUrl && (
                            <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg">
                              <Download className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Link
                  to="/new-document"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                    <FileText className="w-4 h-4 text-primary-600" />
                  </div>
                  <span className="text-slate-700">Request New Document</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                </Link>
                <Link
                  to="/documents"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <Download className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-slate-700">Download Documents</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                </Link>
                <Link
                  to="/profile"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                    <User className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-slate-700">Update Profile</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                </Link>
              </div>
            </div>

            {/* Active Matters */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Your Matters</h3>
              <div className="space-y-3">
                {matters.map((matter) => (
                  <div key={matter.id} className="p-3 bg-slate-50 rounded-lg">
                    <p className="font-medium text-slate-900 text-sm">{matter.name}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-slate-500">{matter.number}</span>
                      <span className="text-xs text-primary-600">{matter.documentCount} docs</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div className="bg-primary-900 rounded-xl p-6 text-white">
              <h3 className="font-semibold mb-2">Need Help?</h3>
              <p className="text-sm text-primary-200 mb-4">
                Our team is here to assist you with any questions.
              </p>
              <div className="space-y-2 text-sm">
                <p className="text-primary-200">
                  <span className="text-white">Email:</span> hello@lawity.com
                </p>
                <p className="text-primary-200">
                  <span className="text-white">Phone:</span> (920) 555-1234
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6 flex items-center justify-between text-sm text-slate-500">
          <p>© {new Date().getFullYear()} Lawity. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="#" className="hover:text-slate-700">Privacy Policy</a>
            <a href="#" className="hover:text-slate-700">Terms of Service</a>
            <a href="#" className="hover:text-slate-700">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}
